import React, { useCallback, useEffect, useRef, useState } from "react";
import { ChevronDown, Loader2, Truck, UserRound, X } from "lucide-react";
import { useTranslation } from "react-i18next";

import { apiResource, userAPI } from "../../services/api";
import type { User } from "../../types";
import type { Vehicle } from "../../types/vehicles";
import { vehicleLabel, vehiclePlateHint } from "../../utils/vehicleLabel";

/** Which directory backs the list — the company's drivers or its trucks. */
export type AssignmentSource = "drivers" | "trucks";

/** All this picker ever hands back: the record, and what to call it. */
export interface AssignmentPick {
  id: string;
  label: string;
  /**
   * A truck's plate, which `label` may not be (it is the fleet name when one is
   * set). Kept for the documents that must print the plate, such as the CMR.
   */
  plate?: string;
}

interface AssignmentSelectProps {
  source: AssignmentSource;
  /** The record currently picked; `null` while nothing is selected. */
  selectedId?: string | null;
  /** Name of the current selection — what the closed box shows. */
  value: string;
  /** Fires only on a real pick from the list. */
  onSelect: (pick: AssignmentPick) => void;
  /** Fires on the X. Omit `allowClear` to take the X away entirely. */
  onClear: () => void;
  /**
   * Whether the selection may be emptied. False on a container that has already
   * started: it may be reassigned, but not left with nobody driving it — a rule
   * the API holds too (REQUIRED_ONCE_STARTED in the line model), and one better
   * shown by having no X than by refusing the save afterwards.
   */
  allowClear?: boolean;
  placeholder?: string;
  disabled?: boolean;
  error?: boolean;
  size?: "sm" | "md";
}

const PAGE_SIZE = 10;

/** One row of either directory, reduced to the two things this picker shows. */
interface Row {
  _id: string;
  label: string;
  /** The muted second line — an email, a make and model. Never a value. */
  hint?: string;
  /** A truck's plate — see `AssignmentPick.plate`. */
  plate?: string;
}

/**
 * A driver / truck picker where the selection — not the text — is the value.
 *
 * The same field as the terminal and company pickers next to it
 * (`DirectorySelect`), and deliberately the same in every respect a dispatcher
 * can feel: nothing typed into the box ever leaves the component. `onSelect`
 * fires only for a row picked from the list, and closing the dropdown throws the
 * search text away and repaints the box with whatever is actually selected. So a
 * half-typed name cannot survive a blur, and editing the text of a saved
 * selection cannot leave the form holding a name with no record behind it.
 *
 * What it does *not* share with `DirectorySelect` is the snapshot. A terminal or
 * a client brings an address, coordinates and contact details down with it,
 * because the truck is sent there; a driver brings a name and a truck brings a
 * plate, and that is the whole of what dispatch needs on the container. So the
 * pick is `{ id, label }` and nothing else is copied.
 *
 * There is no quick-add either. Both directories are near-static — a truck is
 * bought once every few years, a driver is onboarded when one leaves — and both
 * are recorded at that moment, on their own pages. The dead end quick-add exists
 * to avoid on the partner directory is not reachable here.
 *
 * Both lists are paged rather than fuzzy-matched, for the reason `DirectorySelect`
 * gives: the /suggestions endpoints cap out and need two characters before they
 * answer at all, so an empty box would show nothing. This opens on the first page
 * of everything, narrows as the user types, and grows through "Load more".
 */
export const AssignmentSelect: React.FC<AssignmentSelectProps> = ({
  source,
  selectedId,
  value,
  onSelect,
  onClear,
  allowClear = true,
  placeholder,
  disabled = false,
  error = false,
  size = "sm",
}) => {
  const { t } = useTranslation("common");

  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [rows, setRows] = useState<Row[]>([]);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  /**
   * Only the newest request may write to state. Typing fires a fresh page-1
   * load on every debounce tick, and a slow earlier response landing last would
   * otherwise repaint the list with results for a stale search term.
   */
  const requestRef = useRef(0);
  /** Opening loads page 1 itself; the debounce must not repeat that request. */
  const skipDebounceRef = useRef(false);

  const load = useCallback(
    async (search: string, nextPage: number, append: boolean) => {
      const requestId = ++requestRef.current;
      if (append) setLoadingMore(true);
      else setLoading(true);

      try {
        const params = {
          search: search.trim(),
          page: nextPage,
          limit: PAGE_SIZE,
        };

        // Each directory is filtered down to the one role that can be assigned
        // here: a container is driven by a driver, in a truck.
        const response =
          source === "drivers"
            ? await userAPI.getUsers({ ...params, role: "driver" })
            : await apiResource("vehicles").getAll({ ...params, type: "TRUCK" });

        if (requestId !== requestRef.current) return;

        const data = response.data.data;
        const records: Row[] =
          source === "drivers"
            ? ((data.users ?? []) as User[]).map((user) => ({
                _id: user._id,
                label: `${user.firstName ?? ""} ${user.lastName ?? ""}`.trim(),
                hint: user.email,
              }))
            : ((data.vehicles ?? []) as Vehicle[]).map((vehicle) => ({
                _id: vehicle._id,
                label: vehicleLabel(vehicle),
                hint:
                  [vehiclePlateHint(vehicle), vehicle.markAndModel]
                    .filter(Boolean)
                    .join(" · ") || undefined,
                plate: vehicle.registrationNumber ?? "",
              }));

        setRows((prev) => (append ? [...prev, ...records] : records));
        setPage(data.pagination?.page ?? nextPage);
        setPages(data.pagination?.pages ?? 0);
      } catch {
        if (requestId !== requestRef.current) return;
        // A failed lookup leaves the list as it was rather than blanking it;
        // the box stays open so the user can simply try again.
        if (!append) setRows([]);
      } finally {
        if (requestId === requestRef.current) {
          setLoading(false);
          setLoadingMore(false);
        }
      }
    },
    [source],
  );

  const open = useCallback(() => {
    if (disabled || isOpen) return;
    skipDebounceRef.current = true;
    setIsOpen(true);
    setQuery("");
    setRows([]);
    setPage(1);
    setPages(0);
    load("", 1, false);
  }, [disabled, isOpen, load]);

  /**
   * Closing is what discards the typed text: the box only shows `query` while
   * it is open, so reverting to the selected name is automatic.
   */
  const close = useCallback(() => {
    setIsOpen(false);
    setQuery("");
  }, []);

  // Debounced re-search. Skipped on the tick that opened the box, which has
  // already fetched page 1 itself.
  useEffect(() => {
    if (!isOpen) return;
    if (skipDebounceRef.current) {
      skipDebounceRef.current = false;
      return;
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => load(query, 1, false), 400);
    return () => clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, isOpen]);

  useEffect(() => {
    const handleOutside = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) close();
    };
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, [close]);

  useEffect(
    () => () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    },
    [],
  );

  const handleSelect = (row: Row) => {
    onSelect({ id: row._id, label: row.label, plate: row.plate });
    close();
  };

  /** Wipes the selection and closes; clicking the box reopens the list. */
  const handleClear = () => {
    onClear();
    close();
  };

  const searchPlaceholder = t(
    source === "drivers"
      ? "assignmentSelect.searchDrivers"
      : "assignmentSelect.searchTrucks",
  );

  const RowIcon = source === "drivers" ? UserRound : Truck;

  const sizeCls =
    size === "sm" ? "h-8 px-2.5 py-1 text-[13px]" : "h-10 px-3 py-2 text-sm";
  // One border-colour per state rather than layering, for the reason the Input
  // in FormField documents: compiled-CSS ordering, not className ordering,
  // decides which of two border classes wins.
  const boxState = error
    ? `border-signal ${isOpen ? "ring-2 ring-signal/40" : ""}`
    : isOpen
      ? "border-transparent ring-2 ring-copper"
      : "border-[rgba(11,15,14,0.15)]";

  // Read mode renders the picked name as plain text — there is nothing to
  // search, and a live dropdown over a closed record invites accidental edits.
  if (disabled) {
    return (
      <div
        className={`flex w-full items-center truncate rounded-md border border-[rgba(11,15,14,0.15)] bg-bone text-mist ${sizeCls}`}
      >
        {value || "—"}
      </div>
    );
  }

  return (
    <div ref={containerRef} className="relative">
      <div
        className={`flex min-w-0 items-center rounded-md border bg-white ${boxState}`}
      >
        <input
          ref={inputRef}
          type="text"
          // The box shows the selection at rest and the search term only while
          // open, so closing it discards whatever was typed.
          value={isOpen ? query : value}
          onChange={(event) => setQuery(event.target.value)}
          onFocus={open}
          // Focusing does not fire again on a box that is already focused —
          // which it is after a pick — so clicking it has to open too.
          onClick={open}
          onKeyDown={(event) => {
            if (event.key === "Escape") {
              event.preventDefault();
              close();
            }
            if (event.key === "ArrowDown") open();
            // This field sits inside the container's form, where Enter would
            // otherwise submit it — from a box whose text means nothing.
            if (event.key === "Enter" && isOpen) event.preventDefault();
          }}
          placeholder={
            isOpen ? searchPlaceholder : (placeholder ?? searchPlaceholder)
          }
          autoComplete="off"
          className={`min-w-0 flex-1 bg-transparent text-ink outline-none placeholder-mist ${sizeCls}`}
        />

        {/* Both gated on the box being open: a closed box that briefly swapped
            its clear button for a spinner reads as something going wrong. */}
        <div className="flex shrink-0 items-center gap-0.5 pr-1.5">
          {isOpen && loading && (
            <Loader2 className="h-3.5 w-3.5 animate-spin text-mist" />
          )}
          {allowClear && !!value && !(isOpen && loading) && (
            <button
              type="button"
              // Keeps focus in the input so the box does not blur-close between
              // mousedown and click.
              onMouseDown={(event) => event.preventDefault()}
              onClick={handleClear}
              title={t("buttons.clear")}
              className="rounded p-0.5 text-mist hover:text-signal"
              tabIndex={-1}
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
          <button
            type="button"
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => (isOpen ? close() : open())}
            className="rounded p-0.5 text-mist hover:text-ink"
            tabIndex={-1}
          >
            <ChevronDown
              className={`h-4 w-4 transition-transform ${isOpen ? "rotate-180" : ""}`}
            />
          </button>
        </div>
      </div>

      {isOpen && (
        <div className="absolute z-50 mt-1 w-full overflow-hidden rounded-lg border border-[rgba(11,15,14,0.12)] bg-white shadow-xl">
          <div className="max-h-60 overflow-y-auto">
            {loading && rows.length === 0 ? (
              <p className="flex items-center justify-center gap-2 py-5 text-[12px] text-mist">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                {t("directorySelect.loading")}
              </p>
            ) : rows.length === 0 ? (
              <p className="py-5 text-center text-[12px] text-mist">
                {t("directorySelect.noResults")}
              </p>
            ) : (
              rows.map((row) => (
                <button
                  key={row._id}
                  type="button"
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => handleSelect(row)}
                  className={`block w-full border-b border-[rgba(11,15,14,0.06)] px-3 py-2 text-left last:border-b-0 hover:bg-bone ${
                    row._id === selectedId ? "bg-copper/10" : ""
                  }`}
                >
                  <span className="flex items-center gap-1.5">
                    <RowIcon className="h-3.5 w-3.5 shrink-0 text-mist" />
                    <span className="truncate text-[13px] font-medium text-ink">
                      {row.label || "—"}
                    </span>
                  </span>
                  {row.hint && (
                    <span className="mt-0.5 block truncate pl-5 text-[11px] text-mist">
                      {row.hint}
                    </span>
                  )}
                </button>
              ))
            )}
          </div>

          {page < pages && (
            <button
              type="button"
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => load(query, page + 1, true)}
              // Also blocked while page 1 is in flight: the page counter still
              // describes the previous search until that response lands.
              disabled={loadingMore || loading}
              className="flex w-full items-center gap-1.5 border-t border-[rgba(11,15,14,0.08)] px-3 py-2 text-left text-[12px] font-medium text-copper hover:bg-bone disabled:opacity-60"
            >
              {loadingMore && <Loader2 className="h-3 w-3 animate-spin" />}
              {t("directorySelect.loadMore")}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default AssignmentSelect;
